import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, PhoneCall, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { BRAND_HOME_PATH, CONTACT, isWorkshopPagePath, navItems, PHOTO_EDITOR_PATH } from "./config";
import { EditableText } from "./TextEditor";
import { useAuth } from "./auth";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isWorkshopPage = isWorkshopPagePath(location.pathname);
  const isContactsPage = location.pathname === "/contatti";
  const callHref = isWorkshopPage ? CONTACT.workshopHref : CONTACT.roadsideHref;
  const showH24Badge = !isWorkshopPage && !isContactsPage;
  const showCallButton = !isContactsPage;
  const showPrivateAreaButton = isContactsPage;

  const brandLines = (() => {
    if (isContactsPage) {
      return [
        { key: "roadside", textId: "navbar.brand.subtitle.roadside", defaultText: "Soccorso Stradale H24", className: "brand-mark__sub" },
        { key: "workshop", textId: "navbar.brand.subtitle.workshop", defaultText: "Officina Car & Truck", className: "brand-mark__sub brand-mark__sub--workshop" },
      ];
    }

    if (isWorkshopPage) {
      return [
        { key: "workshop", textId: "navbar.brand.subtitle.workshop", defaultText: "Officina Car & Truck", className: "brand-mark__sub brand-mark__sub--workshop" },
      ];
    }

    return [
      { key: "roadside", textId: "navbar.brand.subtitle.roadside", defaultText: "Soccorso Stradale H24", className: "brand-mark__sub" },
    ];
  })();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActiveItem = (item) => {
    if (item.type === "hash") {
      return location.hash === item.href;
    }

    if (location.pathname === item.href) {
      return true;
    }

    return Array.isArray(item.aliases) ? item.aliases.includes(location.pathname) : false;
  };

  const renderNavItem = (item, scope) => {
    const dataTestId = `${scope}-nav-${item.label.toLowerCase().replaceAll(" ", "-")}`;
    const textId = `navbar.${scope}.${item.label}`;
    const className = `nav-link ${isActiveItem(item) ? "nav-link--active" : ""}`;

    if (item.type === "hash") {
      return (
        <a data-testid={dataTestId} key={`${scope}-${item.label}`} href={item.href} className={className}>
          <EditableText textId={textId} defaultText={item.label} as="span" mode="click" />
        </a>
      );
    }

    return (
      <Link data-testid={dataTestId} key={`${scope}-${item.label}`} to={item.href} className={className}>
        <EditableText textId={textId} defaultText={item.label} as="span" mode="click" />
      </Link>
    );
  };

  return (
    <header data-testid="navbar" className={`nav-shell ${scrolled ? "nav-shell--solid" : ""}`}>
      <Link data-testid="navbar-logo-link" to={BRAND_HOME_PATH} className="brand-mark" aria-label="Viscar home pubblicitaria">
        <EditableText textId="navbar.brand.name" defaultText="VISCAR" as="span" mode="click" testId="navbar-logo-text" className="brand-mark__name" />
        <span data-testid="navbar-logo-subtitles" className={`brand-mark__subtitles ${brandLines.length > 1 ? "brand-mark__subtitles--stacked" : ""}`}>
          {brandLines.map((line, index) => (
            <EditableText
              key={line.key}
              textId={line.textId}
              defaultText={line.defaultText}
              as="span"
              mode="click"
              testId={`navbar-logo-subtitle-${line.key}-${index}`}
              className={line.className}
            />
          ))}
        </span>
      </Link>

      <nav data-testid="desktop-navigation" className="desktop-nav" aria-label="Navigazione principale">
        {navItems.map((item) => renderNavItem(item, "desktop"))}
      </nav>

      <div data-testid="navbar-actions" className="nav-actions">
        {showH24Badge ? (
          <span data-testid="navbar-h24-badge" className="operation-badge operation-badge--nav">
            <ShieldCheck size={15} /> <EditableText textId="navbar.badge.h24" defaultText="H24 Operation" as="span" mode="click" />
          </span>
        ) : null}
        {user?.role === "admin" ? (
          <Button data-testid="navbar-photo-panel-link" asChild className="btn-ghost-tech nav-photo-link">
            <a href={PHOTO_EDITOR_PATH}><EditableText textId="navbar.photo.panel" defaultText="Gestione immagini" as="span" mode="click" /></a>
          </Button>
        ) : null}
        {showCallButton ? (
          <Button data-testid="navbar-call-button" asChild className="btn-emergency nav-call">
            <a href={callHref}><PhoneCall size={17} /> <EditableText textId="navbar.call.now" defaultText="Chiama ora" as="span" mode="click" /></a>
          </Button>
        ) : null}
        {showPrivateAreaButton ? (
          <Button data-testid="navbar-private-area-link" asChild className="btn-ghost-tech nav-photo-link">
            <Link to={user ? "/area-riservata" : "/area-riservata/login"}><EditableText textId="navbar.private.area" defaultText="Area riservata" as="span" mode="click" /></Link>
          </Button>
        ) : null}
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="mobile-menu-button" className="mobile-menu-button" aria-label="Apri menu">
              <Menu size={22} />
            </Button>
          </SheetTrigger>
          <SheetContent data-testid="mobile-menu-panel" className={`mobile-sheet ${isWorkshopPage ? "mobile-sheet--workshop" : ""}`.trim()}>
            <SheetHeader>
              <SheetTitle data-testid="mobile-menu-title" className="mobile-sheet-title">
                <span data-testid="mobile-menu-brand-title" className="mobile-sheet-brand">
                  <EditableText textId="navbar.brand.name" defaultText="VISCAR" as="span" mode="click" className="brand-mark__name" />
                  <span data-testid="mobile-menu-brand-subtitles" className={`brand-mark__subtitles ${brandLines.length > 1 ? "brand-mark__subtitles--stacked" : ""}`}>
                    {brandLines.map((line, index) => (
                      <EditableText
                        key={`mobile-${line.key}`}
                        textId={line.textId}
                        defaultText={line.defaultText}
                        as="span"
                        mode="click"
                        testId={`mobile-menu-brand-subtitle-${line.key}-${index}`}
                        className={line.className}
                      />
                    ))}
                  </span>
                </span>
              </SheetTitle>
              <SheetDescription data-testid="mobile-menu-description" className="mobile-sheet-description">
                <EditableText textId="mobile.menu.description" defaultText="Accesso rapido a servizi, flotta operativa, copertura territoriale e contatti H24." as="span" mode="click" multiline />
              </SheetDescription>
            </SheetHeader>
            <div data-testid="mobile-navigation" className="mobile-nav-list">
              {navItems.map((item) => (
                <SheetClose asChild key={item.label}>
                  {item.type === "hash" ? (
                    <a data-testid={`mobile-nav-${item.label.toLowerCase().replaceAll(" ", "-")}`} href={item.href} className={`nav-link ${isActiveItem(item) ? "nav-link--active" : ""}`}>
                      <EditableText textId={`navbar.mobile.${item.label}`} defaultText={item.label} as="span" mode="click" />
                    </a>
                  ) : (
                    <Link data-testid={`mobile-nav-${item.label.toLowerCase().replaceAll(" ", "-")}`} to={item.href} className={`nav-link ${isActiveItem(item) ? "nav-link--active" : ""}`}>
                      <EditableText textId={`navbar.mobile.${item.label}`} defaultText={item.label} as="span" mode="click" />
                    </Link>
                  )}
                </SheetClose>
              ))}
              {user?.role === "admin" ? (
                <SheetClose asChild>
                  <a data-testid="mobile-menu-photo-panel-link" href={PHOTO_EDITOR_PATH}><EditableText textId="mobile.menu.photo.panel" defaultText="Gestione immagini" as="span" mode="click" /></a>
                </SheetClose>
              ) : null}
              {showCallButton ? (
                <SheetClose asChild>
                  <a data-testid="mobile-menu-call-link" className={`mobile-sheet-cta ${isWorkshopPage ? "mobile-sheet-cta--workshop" : ""}`.trim()} href={callHref}>
                    <PhoneCall size={18} /> <EditableText textId="mobile.menu.call" defaultText="Chiama ora" as="span" mode="click" />
                  </a>
                </SheetClose>
              ) : null}
              {showPrivateAreaButton ? (
                <SheetClose asChild>
                  <Link data-testid="mobile-menu-private-area-link" to={user ? "/area-riservata" : "/area-riservata/login"}><EditableText textId="mobile.menu.private.area" defaultText="Area riservata" as="span" mode="click" /></Link>
                </SheetClose>
              ) : null}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};